import React from 'react';
import {
  FiDollarSign as DollarSign,
  FiShield as Shield,
  FiTrendingUp as TrendingUp,
  FiCheckCircle as CheckCircle
} from 'react-icons/fi';

const CoverageComparisonChart = ({ policies, compareFeature }) => {
  // Get comparison data for charts
  const premiumData = compareFeature(policies, 'premium');
  const deductibleData = compareFeature(policies, 'deductible');
  const coverageData = compareFeature(policies, 'coverage');

  // Calculate max values for scaling
  const maxPremium = Math.max(...premiumData.map(d => d.value));
  const maxDeductible = Math.max(...deductibleData.map(d => d.value));
  const maxCoverage = Math.max(...coverageData.map(d => d.value));

  // Colors for each policy
  const colors = [
    { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600', light: 'bg-blue-100' },
    { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-600', light: 'bg-purple-100' },
    { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-600', light: 'bg-green-100' }
  ];

  // Coverage features breakdown
  return (
    <div className="space-y-6">
      {/* Premium Comparison Bar Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <DollarSign size={20} className="text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Premium Comparison</h3>
            <p className="text-sm text-gray-600">Monthly/Annual payment amounts</p>
          </div>
        </div>

        <div className="space-y-4">
          {policies.map((policy, index) => {
            const premium = premiumData[index];
            const percentage = (premium.value / maxPremium) * 100;

            return (
              <div key={policy.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${colors[index].bg}`}></div>
                    <span className="font-medium text-gray-900">{policy.policyType}</span>
                  </div>
                  <span className="font-bold text-gray-900">{premium.display}</span>
                </div>
                <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                  <div
                    className={`absolute inset-y-0 left-0 ${colors[index].bg} transition-all duration-500 flex items-center justify-end px-3`}
                    style={{ width: `${percentage}%` }}
                  >
                    <span className="text-white text-sm font-medium">
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deductible & Coverage Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Deductible Comparison */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <TrendingUp size={20} className="text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Deductible</h3>
              <p className="text-sm text-gray-600">Out-of-pocket cost</p>
            </div>
          </div>

          <div className="space-y-4">
            {policies.map((policy, index) => {
              const deductible = deductibleData[index];
              const percentage = (deductible.value / maxDeductible) * 100;

              return (
                <div key={policy.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${colors[index].bg}`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        {policy.vendor}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{deductible.display}</div>
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${colors[index].bg} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Coverage Amount Comparison */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Shield size={20} className="text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Coverage Amount</h3>
              <p className="text-sm text-gray-600">Protection limit</p>
            </div>
          </div>

          <div className="space-y-4">
            {policies.map((policy, index) => {
              const coverage = coverageData[index];
              const percentage = (coverage.value / maxCoverage) * 100;

              return (
                <div key={policy.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${colors[index].bg}`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        {policy.vendor}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-gray-900">{coverage.display}</div>
                    </div>
                  </div>
                  <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${colors[index].bg} transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Coverage Features Matrix */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Coverage Features Matrix</h3>
          <p className="text-sm text-gray-600">Additional coverage options included</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">
                  Feature
                </th>
                {policies.map((policy, index) => (
                  <th key={policy.id} className="text-center py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${colors[index].bg}`}></div>
                      <span className="text-sm font-semibold text-gray-900">
                        {policy.policyType}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {['Collision', 'Comprehensive', 'Roadside', 'Rental'].map((feature) => (
                <tr key={feature} className="hover:bg-gray-50">
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">
                    {feature}
                  </td>
                  {policies.map((policy, index) => {
                    const hasFeature = policy.coverageDetails?.additionalCoverage?.includes(
                      feature.toLowerCase()
                    );
                    return (
                      <td key={policy.id} className="py-4 px-4 text-center">
                        {hasFeature ? (
                          <div className="flex items-center justify-center">
                            <div className={`w-8 h-8 ${colors[index].light} rounded-full flex items-center justify-center`}>
                              <CheckCircle size={18} className={colors[index].text} />
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-300">—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default CoverageComparisonChart;